import React, { useContext } from "react";
import MobileFrame from "./MobileFrame";
import { AppContext } from "../../../../context/app";
import { useGetClientGymTypeSubType } from "../../../../apis/clientApi.hooks";
import { getClassSuggestions } from "../../helpers";

interface StaffAppDemoProps {
  formData: any;
}

const StaffAppDemo: React.FC<StaffAppDemoProps> = ({ formData }) => {
  const { user } = useContext(AppContext);
  const name = user?.first_name + " " + user?.last_name;

  const { data: clientGymTypeSubType, isLoading: isLoadingGymType } =
    useGetClientGymTypeSubType();
  const availableClasses = getClassSuggestions(
    clientGymTypeSubType?.parentGymType?.label,
    clientGymTypeSubType?.subtype?.label,
  );

  const getCurrentDate = () => {
    const today = new Date();
    return {
      month: today.toLocaleDateString("en-US", { month: "long" }),
      day: today.getDate(),
      weekday: today.toLocaleDateString("en-US", { weekday: "long" }),
    };
  };

  const generateWeekDays = () => {
    const today = new Date();
    const days = [];

    // Show 5 days centered on today (2 days before, today, 2 days after)
    for (let i = -2; i <= 2; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      days.push({
        date: date.getDate(),
        weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
        isToday: i === 0, // Middle day is today
      });
    }

    return days;
  };

  const generateClasses = () => {
    const classTypes = formData?.selectedClassTypes || [];
    const appointmentTypes = formData?.selectedAppointmentTypes || [];
    const allTypes = [...classTypes, ...appointmentTypes];

    // Use gym type-based suggestions if no classes are selected
    const classesToUse = allTypes.length > 0 ? allTypes : availableClasses;

    const times = [
      "10:00 AM - 11:00 AM",
      "11:00 AM - 12:00 PM",
      "2:00 PM - 3:00 PM",
      "3:00 PM - 4:00 PM",
      "5:00 PM - 6:00 PM",
    ];
    const instructors = [
      name,
      "Nolan Parks",
      "Tim Johnson",
      "Mike Chen",
      "Amy Lee",
    ];
    const capacities = ["25/30", "21/25", "3/12", "5/8", "2/15"];

    return classesToUse.slice(0, 5).map((type, index) => ({
      id: index + 1,
      time: times[index] || "10:00 AM - 11:00 AM",
      title: type,
      instructor: instructors[index % instructors.length],
      initials: instructors[index % instructors.length]
        .split(" ")
        .map((n) => n[0])
        .join(""),
      capacity: capacities[index % capacities.length],
    }));
  };

  const classes = generateClasses();

  const currentDate = getCurrentDate();
  const weekDays = generateWeekDays();

  // Show loading state if gym type data is still loading
  if (isLoadingGymType) {
    return (
      <MobileFrame
        brandColor={formData?.color || "#3B82F6"}
        showBottomNavigation={true}
      >
        <div className="tw-h-full tw-bg-white tw-flex tw-items-center tw-justify-center">
          <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-neutral-800"></div>
        </div>
      </MobileFrame>
    );
  }

  return (
    <MobileFrame
      brandColor={formData?.color || "#3B82F6"}
      showBottomNavigation={true}
    >
      <div className="tw-h-full tw-bg-gray-50 tw-flex tw-flex-col">
        {/* Header */}
        <div
          className="tw-p-4 tw-text-white"
          style={{ backgroundColor: formData?.color || "#3B82F6" }}
        >
          <div className="tw-flex tw-items-center tw-justify-between">
            <button className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center">
              <svg
                className="tw-w-6 tw-h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <h1 className="tw-text-lg tw-font-semibold tw-text-white">
              Classes
            </h1>
            <div className="tw-w-8 tw-h-8"></div>
          </div>
        </div>

        {/* Date Scroll */}
        <div className="tw-px-2 tw-py-2">
          <div className="tw-flex tw-justify-between">
            {weekDays.map((day, index) => (
              <div
                key={index}
                className="tw-flex tw-flex-col tw-items-center tw-py-2 tw-text-gray-600"
              >
                <span className="tw-text-xs tw-font-medium">{day.weekday}</span>
                <span
                  className={`tw-text-sm tw-font-semibold tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center ${
                    day.isToday ? "tw-text-white" : "tw-text-gray-600"
                  }`}
                  style={{
                    backgroundColor: day.isToday
                      ? formData?.color || "#3B82F6"
                      : "transparent",
                  }}
                >
                  {day.date}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Date Header */}
        <div className="tw-px-4 tw-py-2">
          <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700">
            {currentDate.weekday}, {currentDate.month} {currentDate.day}
          </h2>
          <div className="tw-flex tw-items-center tw-justify-center tw-mt-2">
            <svg
              className="tw-w-4 tw-h-4 tw-text-gray-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>

        {/* Classes List */}
        <div className="tw-flex-1 tw-px-4 tw-pb-4">
          {classes.map((classItem) => (
            <div key={classItem.id} className="tw-mb-6">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <span className="tw-text-xs tw-font-medium tw-text-gray-700">
                  {classItem.time}
                </span>
                <span className="tw-text-xs tw-text-gray-500 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded">
                  {classItem.capacity}
                </span>
              </div>

              <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-shadow-sm">
                <h3 className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-3">
                  {classItem.title}
                </h3>

                <div className="tw-flex tw-items-center">
                  <div className="tw-w-10 tw-h-10 tw-bg-gray-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mr-3">
                    <span className="tw-text-white tw-font-medium tw-text-sm">
                      {classItem.initials}
                    </span>
                  </div>
                  <span className="tw-text-xs tw-font-medium tw-text-gray-700">
                    {classItem.instructor}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </MobileFrame>
  );
};

export default StaffAppDemo;
